import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { nameRE } from "@/constants/regex";
import { isStrictValidEmail } from "@/lib/helpers/email-validation";
import { normalizeEmail } from "@/lib/helpers/normalize-email";
import { superRefinePhoneNumber } from "@/lib/helpers/phone-number-validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const signUpSchema = z
  .object({
    firstName: z
      .string()
      .min(1, "First name is required")
      .refine((value) => nameRE.test(value), {
        error: "Please avoid using special characters",
      }),
    lastName: z
      .string()
      .min(1, "Last name is required")
      .refine((value) => nameRE.test(value), {
        error: "Please avoid using special characters",
      }),
    phoneNumber: z.string().min(1, "Phone number is required").min(6, "Invalid phone number"),
    email: z
      .string()
      .transform(normalizeEmail)
      .pipe(
        z
          .string()
          .min(1, "Email is required")
          .refine((value) => isStrictValidEmail(value), {
            error: "Invalid email",
          }),
      ),
  })
  .superRefine(async (arg, ctx) => {
    await superRefinePhoneNumber({
      phoneNumber: arg.phoneNumber,
      ctx,
    });
  });

export type SignUpFormData = z.input<typeof signUpSchema>;

export interface SignUpFormReturn extends UseFormReturn<SignUpFormData> {
  defaultValues: SignUpFormData;
}

const initialValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "+65",
  email: "",
};

export const useSignUpForm = (): SignUpFormReturn => ({
  defaultValues: initialValues,
  ...useForm<SignUpFormData>({
    mode: "onChange",
    defaultValues: initialValues,
    resolver: zodResolver(signUpSchema),
  }),
});
